import * as React from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Descriptions from 'antd/lib/descriptions';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import Switch from 'antd/lib/switch';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import type { AppComponentProps } from '@/typescriptHelpers';
import type { RootState } from '@/reducers/reducers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { userIsRunwayAdmin } from '@/helpers';
import {
    selectOrgSettings,
    selectOrgUsers,
    selectUser,
    selectUserCanPerformAction,
    selectUserIsAdmin,
} from '@/selectors/selectors';
import {
    hotfixRequestErrorMessage,
    useGetHotfixRequestSettingsQuery,
    useUpdateHotfixRequestSettingsMutation,
} from '@/middlewares/defaultApi';
import type { HotfixRequestFormFieldSettings, HotfixRequestSettings } from '@/types/hotfixRequests';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { globalMessage } from '@/utils/globalMessage';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import RequestFormFieldName from './RequestFormFieldName';

const HotfixRequestsSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const scope = { orgId, appId };
    const query = useGetHotfixRequestSettingsQuery(scope);
    const [updateSettings, update] = useUpdateHotfixRequestSettingsMutation();
    const [approvalsOpen, setApprovalsOpen] = React.useState(false);
    const [fieldsOpen, setFieldsOpen] = React.useState(false);
    const [approvalsForm] = Form.useForm();
    const [fieldsForm] = Form.useForm();
    const enabled = Form.useWatch('hotfixApprovalsEnabled', approvalsForm) ?? false;
    const selectedApprovers: string[] = Form.useWatch('approvers', approvalsForm) ?? [];
    const watchedFormFields = Form.useWatch('hotfixRequestFormFieldsSettings', fieldsForm);
    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, orgId));
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, orgId));
    const isAdmin = useSelector(selectUserIsAdmin);
    const user = useSelector(selectUser);
    const runwayAdminCanEdit = useSelector(
        (state: RootState) =>
            userIsRunwayAdmin(user) &&
            selectUserCanPerformAction(
                state,
                RunwayUserAction.UAUpdateAppSettings,
                undefined,
                undefined,
                undefined,
                false,
                appId,
            ),
    );
    const canEdit = isAdmin || runwayAdminCanEdit;
    const groups = React.useMemo(() => orgSettings?.groups ?? {}, [orgSettings?.groups]);
    const settings = React.useMemo(() => {
        if (!query.currentData) return undefined;
        return {
            ...query.currentData,
            hotfixApprovalRequiredApproverUserIds: query.currentData.hotfixApprovalRequiredApproverUserIds ?? [],
            hotfixApprovalRequiredApproverUserGroupIds:
                query.currentData.hotfixApprovalRequiredApproverUserGroupIds ?? [],
            hotfixRequestFormFieldsSettings: query.currentData.hotfixRequestFormFieldsSettings ?? [],
        };
    }, [query.currentData]);

    const approverOptions = React.useMemo(
        () => [
            ...Object.values(groups).map((group) => ({ label: `Group: ${group.name}`, value: `group:${group.id}` })),
            ...orgUsers.map((user) => ({
                label: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
                value: `user:${user.id}`,
            })),
        ],
        [groups, orgUsers],
    );

    const openApprovalsEditor = () => {
        if (!settings) return;
        approvalsForm.setFieldsValue({
            hotfixApprovalsEnabled: settings.hotfixApprovalsEnabled,
            requiredHotfixRequestApprovals: settings.requiredHotfixRequestApprovals,
            hotfixApprovalImplicitCreatorApproval: settings.hotfixApprovalImplicitCreatorApproval,
            approvers: [
                ...settings.hotfixApprovalRequiredApproverUserGroupIds.map((id) => `group:${id}`),
                ...settings.hotfixApprovalRequiredApproverUserIds.map((id) => `user:${id}`),
            ],
        });
        setApprovalsOpen(true);
    };

    const saveApprovals = async (
        values: Pick<HotfixRequestSettings, 'hotfixApprovalsEnabled' | 'requiredHotfixRequestApprovals'> & {
            approvers: string[];
            hotfixApprovalImplicitCreatorApproval: boolean;
        },
    ) => {
        const approvers = values.approvers ?? [];
        try {
            await updateSettings({
                ...scope,
                hotfixApprovalsEnabled: values.hotfixApprovalsEnabled,
                requiredHotfixRequestApprovals: values.requiredHotfixRequestApprovals,
                hotfixApprovalRequiredApproverUserGroupIds: approvers
                    .filter((value) => value.startsWith('group:'))
                    .map((value) => value.slice('group:'.length)),
                hotfixApprovalRequiredApproverUserIds: approvers
                    .filter((value) => value.startsWith('user:'))
                    .map((value) => value.slice('user:'.length)),
                hotfixApprovalImplicitCreatorApproval:
                    values.hotfixApprovalsEnabled &&
                    approvers.length > 0 &&
                    values.hotfixApprovalImplicitCreatorApproval,
            }).unwrap();
            globalMessage.success('Hotfix approval settings saved.');
            setApprovalsOpen(false);
        } catch {
            // The safe backend message is rendered in the modal.
        }
    };

    const openFieldsEditor = () => {
        if (!settings) return;
        fieldsForm.setFieldsValue({ hotfixRequestFormFieldsSettings: settings.hotfixRequestFormFieldsSettings });
        setFieldsOpen(true);
    };

    const saveFields = async (values: Pick<HotfixRequestSettings, 'hotfixRequestFormFieldsSettings'>) => {
        try {
            await updateSettings({
                ...scope,
                hotfixRequestFormFieldsSettings: (values.hotfixRequestFormFieldsSettings ?? []).map((field) => ({
                    ...field,
                    minCharacters: field.required ? field.minCharacters : 0,
                })),
            }).unwrap();
            globalMessage.success('Hotfix request form fields saved.');
            setFieldsOpen(false);
        } catch {
            // The safe backend message is rendered in the modal.
        }
    };

    if (query.isLoading && !settings) return <Spin />;
    if (!settings) {
        return <Alert type="error" message={hotfixRequestErrorMessage(query.error)} />;
    }

    const approverNames = [
        ...settings.hotfixApprovalRequiredApproverUserGroupIds.map((id) => groups[id]?.name ?? id),
        ...settings.hotfixApprovalRequiredApproverUserIds.map((id) => {
            const user = orgUsers.find((candidate) => candidate.id === id);
            return [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || id;
        }),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Descriptions
                title="Hotfix approvals"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={
                    <Button type="link" icon={<EditOutlined />} onClick={openApprovalsEditor}>
                        Edit
                    </Button>
                }
            >
                <Descriptions.Item label="Hotfix approvals required">
                    {settings.hotfixApprovalsEnabled ? 'Yes' : 'No'}
                </Descriptions.Item>
                {settings.hotfixApprovalsEnabled && (
                    <>
                        <Descriptions.Item label="Number of approvals required">
                            {settings.requiredHotfixRequestApprovals}
                        </Descriptions.Item>
                        <Descriptions.Item label="Specific approvers">
                            {approverNames.length > 0 ? approverNames.join(', ') : 'Any eligible app member'}
                        </Descriptions.Item>
                        {approverNames.length > 0 && (
                            <Descriptions.Item label="Implicit approval when a required approver creates a hotfix request">
                                {settings.hotfixApprovalImplicitCreatorApproval ? 'Yes' : 'No'}
                            </Descriptions.Item>
                        )}
                    </>
                )}
            </Descriptions>
            <Descriptions
                title="Hotfix request form template"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={
                    <Button type="link" icon={<EditOutlined />} onClick={openFieldsEditor}>
                        Edit
                    </Button>
                }
            >
                {settings.hotfixRequestFormFieldsSettings.length === 0 ?
                    <Descriptions.Item label="Fields">No custom text fields</Descriptions.Item>
                :   settings.hotfixRequestFormFieldsSettings.map((field) => (
                        <Descriptions.Item key={field.name} label={<RequestFormFieldName name={field.name} />}>
                            {field.required ? 'Required' : 'Optional'}
                            {field.minCharacters ? ` · Minimum ${field.minCharacters} characters` : ''}
                        </Descriptions.Item>
                    ))
                }
            </Descriptions>
            <Modal
                title="Hotfix approvals"
                open={approvalsOpen}
                width={680}
                destroyOnClose
                onCancel={() => setApprovalsOpen(false)}
                onOk={approvalsForm.submit}
                okText="Save"
                okButtonProps={{ loading: update.isLoading }}
                footer={canEdit ? undefined : null}
            >
                {update.error && <Alert type="error" message={hotfixRequestErrorMessage(update.error)} />}
                <Text type="secondary">
                    Changes to these settings will not effect hotfix requests already in progress.
                </Text>
                <Form
                    form={approvalsForm}
                    layout="vertical"
                    onFinish={saveApprovals}
                    style={{ marginTop: 16 }}
                    disabled={!canEdit}
                >
                    <Form.Item name="hotfixApprovalsEnabled" label="Hotfix approvals required" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    {enabled && (
                        <>
                            <Form.Item
                                name="requiredHotfixRequestApprovals"
                                label="Number of approvals required"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} />
                            </Form.Item>
                            <Form.Item name="approvers" label="Specific users or groups allowed to approve">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    options={approverOptions}
                                    placeholder="Select users and/or groups"
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                            {selectedApprovers.length > 0 && (
                                <Form.Item name="hotfixApprovalImplicitCreatorApproval" valuePropName="checked">
                                    <Checkbox>
                                        Provide implicit approval if a required approver creates a hotfix request
                                    </Checkbox>
                                </Form.Item>
                            )}
                        </>
                    )}
                </Form>
            </Modal>
            <Modal
                title="Hotfix request form fields"
                open={fieldsOpen}
                width={680}
                destroyOnClose
                onCancel={() => setFieldsOpen(false)}
                onOk={fieldsForm.submit}
                okText="Save"
                okButtonProps={{ loading: update.isLoading }}
                styles={{
                    body: { padding: '24px 24px 0' },
                    footer: { marginTop: 0, padding: '0 24px 16px' },
                }}
                footer={canEdit ? undefined : null}
            >
                {update.error && <Alert type="error" message={hotfixRequestErrorMessage(update.error)} />}
                <Form
                    form={fieldsForm}
                    layout="vertical"
                    onFinish={saveFields}
                    style={{ marginTop: 16 }}
                    disabled={!canEdit}
                >
                    <Form.List
                        name="hotfixRequestFormFieldsSettings"
                        rules={[
                            {
                                validator: async (_, fields: HotfixRequestFormFieldSettings[] = []) => {
                                    const names = fields
                                        .map((field) => field?.name?.trim().toLowerCase())
                                        .filter(Boolean);
                                    if (new Set(names).size !== names.length)
                                        throw new Error('Field names must be unique');
                                },
                            },
                        ]}
                    >
                        {(fields, { add, remove }, { errors }) => (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {fields.map(({ key, name, ...rest }) => (
                                    <div
                                        key={key}
                                        style={{
                                            border: `1px solid ${antGrey.grey4}`,
                                            borderRadius: 8,
                                            padding: 12,
                                            backgroundColor: runwayColors.white,
                                        }}
                                    >
                                        <Button
                                            type="text"
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            onClick={() => remove(name)}
                                            style={{ float: 'right' }}
                                        />
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'name']}
                                            label="Field name"
                                            rules={[{ required: true }]}
                                        >
                                            <Input style={{ width: '50%' }} />
                                        </Form.Item>
                                        <Form.Item {...rest} name={[name, 'required']} valuePropName="checked">
                                            <Checkbox
                                                onChange={(event) => {
                                                    if (!event.target.checked) {
                                                        fieldsForm.setFieldValue(
                                                            ['hotfixRequestFormFieldsSettings', name, 'minCharacters'],
                                                            0,
                                                        );
                                                    }
                                                }}
                                            >
                                                Required
                                            </Checkbox>
                                        </Form.Item>
                                        {watchedFormFields?.[name]?.required && (
                                            <Form.Item
                                                {...rest}
                                                name={[name, 'minCharacters']}
                                                label="Minimum characters"
                                            >
                                                <InputNumber min={0} placeholder="None" />
                                            </Form.Item>
                                        )}
                                        <Form.Item {...rest} name={[name, 'description']} label="Description">
                                            <MarkdownEditor
                                                preview="edit"
                                                textareaProps={{
                                                    placeholder:
                                                        'Optional helper shown with the field. Markdown is supported.',
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item {...rest} name={[name, 'placeholder']} label="Placeholder text">
                                            <Input.TextArea rows={2} placeholder="Optional placeholder text" />
                                        </Form.Item>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'prePopulatedText']}
                                            label="Pre-populated text"
                                        >
                                            <Input.TextArea
                                                rows={3}
                                                placeholder="Optional pre-populated text (e.g. for a template)"
                                            />
                                        </Form.Item>
                                    </div>
                                ))}
                                <Button
                                    icon={<PlusOutlined />}
                                    onClick={() =>
                                        add({
                                            name: '',
                                            required: false,
                                            minCharacters: 0,
                                            description: '',
                                            placeholder: '',
                                            prePopulatedText: '',
                                        })
                                    }
                                >
                                    Add field
                                </Button>
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </div>
    );
};

export default HotfixRequestsSettings;
